// BiShell port from workday-poc campaigns.jsx:4-53 — ESM, props-driven nav.
// Rendered as BenInsights top tabs; the dev-only tools (role + data mode) live in
// the footer, matching wae-ui; a per-page topbar control renders at the tab-row right.
import { useEffect, useState } from "react";
import { Tab, Tabs } from "@mui/material";
import { Link } from "react-router-dom";
import { tabsNoUnderlineSx, tabNoUnderlineSx } from "common/tabSx";
import { Icon } from "./Icon.jsx";
import { ACTIVATION_PATHS } from "../navConfig.js";
import {
  getDevelopmentDataMode,
  getDevelopmentRoles,
  resetDevelopmentRoles,
  setDevelopmentDataMode,
  setDevelopmentRoles,
} from "../api/client.js";

const NAV = [
  { key: "overview", label: "Overview", path: ACTIVATION_PATHS.overview },
  { key: "enrollment", label: "Enrollment", path: ACTIVATION_PATHS.enrollment },
  { key: "opportunities", label: "Opportunities", path: ACTIVATION_PATHS.opportunities },
  { key: "campaigns", label: "Campaigns", path: ACTIVATION_PATHS.campaigns },
  { key: "monitoring", label: "Monitoring", path: ACTIVATION_PATHS.monitoring },
];

function developmentDataModeLabel(mode) {
  if (mode.id === "mysql") return "Live data";
  return mode.label;
}

// Development-only tools (role + Live/Demo data switch). Each getter returns null
// when its /dev endpoint is absent (e.g. production), so this self-hides.
function DevelopmentTools() {
  const [roleContext, setRoleContext] = useState(null);
  const [dataContext, setDataContext] = useState(null);
  const [savingRole, setSavingRole] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [roleError, setRoleError] = useState("");
  const [dataError, setDataError] = useState("");

  useEffect(() => {
    let active = true;
    getDevelopmentRoles()
      .then((result) => {
        if (active) setRoleContext(result);
      })
      .catch((requestError) => {
        if (active) setRoleError(requestError.message || "Role switcher unavailable");
      });
    getDevelopmentDataMode()
      .then((result) => {
        if (active) setDataContext(result);
      })
      .catch((requestError) => {
        if (active) setDataError(requestError.message || "Data switcher unavailable");
      });
    return () => {
      active = false;
    };
  }, []);

  if (!roleContext && !dataContext) return null;

  const activeRoles = roleContext?.identity?.roles || [];
  const value = activeRoles.length === 1 ? activeRoles[0] : "__configured";

  const changeRole = async (event) => {
    const nextRole = event.target.value;
    if (!nextRole || nextRole === "__configured") return;
    setSavingRole(true);
    setRoleError("");
    try {
      const result =
        nextRole === "__reset" ? await resetDevelopmentRoles() : await setDevelopmentRoles([nextRole]);
      setRoleContext(result);
      window.location.reload();
    } catch (requestError) {
      setRoleError(requestError.message || "Could not change development role");
      setSavingRole(false);
    }
  };

  const changeDataMode = async (event) => {
    const nextMode = event.target.value;
    if (!nextMode || nextMode === dataContext?.activeMode) return;
    setSavingData(true);
    setDataError("");
    try {
      const result = await setDevelopmentDataMode(nextMode);
      setDataContext(result);
      window.location.reload();
    } catch (requestError) {
      setDataError(requestError.message || "Could not change test data");
      setSavingData(false);
    }
  };

  return (
    <div className="bi-dev-role-switch" title={dataError || roleError || "Development-only test controls"}>
      <span className="bi-dev-badge" aria-hidden="true">
        DEV
      </span>
      {dataContext && (
        <label className="bi-dev-role-label">
          <span>Test data</span>
          <select
            className="bi-dev-role-select bi-dev-data-select"
            value={dataContext.activeMode}
            onChange={changeDataMode}
            disabled={savingData}
            aria-describedby={dataError ? "dev-data-switch-error" : undefined}
          >
            {(dataContext.availableModes || []).map((mode) => (
              <option key={mode.id} value={mode.id}>
                {developmentDataModeLabel(mode)}
              </option>
            ))}
          </select>
        </label>
      )}
      {roleContext && (
        <label className="bi-dev-role-label">
          <span>Test role</span>
          <select
            className="bi-dev-role-select"
            value={value}
            onChange={changeRole}
            disabled={savingRole}
            aria-describedby={roleError ? "dev-role-switch-error" : undefined}
          >
            {activeRoles.length !== 1 && (
              <option value="__configured">Configured roles ({activeRoles.length})</option>
            )}
            {(roleContext.availableRoles || []).map((role) => (
              <option key={role.id} value={role.id}>
                {role.label}
              </option>
            ))}
            {roleContext.overridden && <option value="__reset">Reset configured roles</option>}
          </select>
        </label>
      )}
      <span id="dev-data-switch-error" className="sr-only" role="status" aria-live="polite">
        {savingData ? "Changing test data" : dataError}
      </span>
      <span id="dev-role-switch-error" className="sr-only" role="status" aria-live="polite">
        {savingRole ? "Changing development role" : roleError}
      </span>
    </div>
  );
}

export function Shell({ title, subtitle, activeNav, children, headerRight, topbarControl, tenant, auth }) {
  const nav = auth?.canViewRestrictedOpportunities
    ? [...NAV, { key: "secure-review", label: "Secure Review", path: ACTIVATION_PATHS.secureReview }]
    : NAV;
  // MUI Tabs select by key. Sub-routes (campaigns/new, campaigns/:id) pass
  // activeNav="campaigns" so the Campaigns tab stays selected; how-to and
  // methodology are footer pages with no tab, so fall back to `false`.
  // Pre-rehaul screens still pass "activation" for the campaigns area.
  const normalizedNav = activeNav === "activation" ? "campaigns" : activeNav;
  const tabValue = nav.some((item) => item.key === normalizedNav) ? normalizedNav : false;

  return (
    <div className="bi-app2">
      <a className="bi-skip-link" href="#main-content">
        Skip to main content
      </a>

      <div className="bi-main2">
        <div className="ae-tab-bar">
          <Tabs
            value={tabValue}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Activate sections"
            sx={tabsNoUnderlineSx}
          >
            {nav.map((item) => (
              <Tab
                key={item.key}
                label={item.label}
                value={item.key}
                component={Link}
                to={item.path}
                sx={tabNoUnderlineSx}
              />
            ))}
          </Tabs>
          {topbarControl}
        </div>

        <div className="bi-title2">
          <div>
            <h1>{title}</h1>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <div className="bi-title-actions">{headerRight}</div>
        </div>

        <main className="bi-content2 fade-in" id="main-content" tabIndex={-1}>
          {children}
        </main>

        <footer className="bi-footer2">
          <span>
            {activeNav === "secure-review"
              ? "Restricted member opportunity review · tenant-scoped · access audited · Activate by Claritev"
              : "Opportunities summarized from approved benefits and claims evidence (MSK · Diabetes Management · Virtual Care) · aggregate-only benefits view · Activate by Claritev"}
          </span>
          <div className="bi-footer-links">
            {process.env.NODE_ENV === "development" && <DevelopmentTools />}
            <Link className={activeNav === "how-to" ? "active" : ""} to={ACTIVATION_PATHS.howTo}>
              <Icon name="list" size={13} strokeWidth={1.8} /> How-to Guide
            </Link>
            <Link className={activeNav === "methodology" ? "active" : ""} to={ACTIVATION_PATHS.methodology}>
              <Icon name="doc" size={13} strokeWidth={1.8} /> Methodology &amp; FAQ
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
